import React, { useCallback, useState } from 'react';

const DEFAULT_ROLLING_LOOKBACK: PerformanceBoardTypes.SlideOut.RollingLookback =
  {
    type: 'last',
    interval: 'week',
    n: 13,
  };

const DEFAULT_DATE_FILTER: PerformanceBoardTypes.SlideOut.SlideOutDateFilter = {
  mode: 'Periods',
  interval: { mode: 'Current Interval', isToDate: false },
};

/**
 * Wizard state for a slide-out section's "Date filter mode" (Periods / Rolling)
 * and query date field. Shared by the Related Documents and Simple Grid wizards.
 */
const useSlideoutDateFilterState = ({
  initial,
}: {
  initial: PerformanceBoardTypes.SlideOut.SlideOutDateFilter | undefined;
}) => {
  const [dateFilter, setDateFilter] =
    useState<PerformanceBoardTypes.SlideOut.SlideOutDateFilter>(
      initial ?? DEFAULT_DATE_FILTER,
    );

  const onDateFilterModeChanged = useCallback(
    (mode: PerformanceBoardTypes.SlideOut.SlideOutDateFilter['mode']) => {
      setDateFilter((current) => {
        if (mode === current.mode) {
          return current;
        }
        if (mode === 'Rolling') {
          return {
            mode: 'Rolling',
            lookback: DEFAULT_ROLLING_LOOKBACK,
            dateField: current.dateField,
          };
        }
        return {
          mode: 'Periods',
          interval: { mode: 'Current Interval', isToDate: false },
          dateField: current.dateField,
        };
      });
    },
    [],
  );

  // Adapter for the shared IntervalPicker (Periods mode only) — keeps its
  // SlideOutDateRange types intact.
  const setInterval: React.Dispatch<
    React.SetStateAction<PerformanceBoardTypes.SlideOut.SlideOutDateRange>
  > = useCallback((action) => {
    setDateFilter((current) => {
      if (current.mode !== 'Periods') {
        return current;
      }
      const nextInterval =
        typeof action === 'function' ? action(current.interval) : action;
      return { ...current, interval: nextInterval };
    });
  }, []);

  // Lookback setter for RollingDateInput (Rolling mode only).
  const setLookback = useCallback(
    (next: PerformanceBoardTypes.SlideOut.RollingLookback) => {
      setDateFilter((current) =>
        current.mode === 'Rolling' ? { ...current, lookback: next } : current,
      );
    },
    [],
  );

  // Date field applies to whichever variant is active.
  const setDateField: React.Dispatch<React.SetStateAction<string | undefined>> =
    useCallback((action) => {
      setDateFilter((current) => {
        const nextField =
          typeof action === 'function' ? action(current.dateField) : action;
        return { ...current, dateField: nextField };
      });
    }, []);

  return {
    dateFilter,
    onDateFilterModeChanged,
    setInterval,
    setLookback,
    setDateField,
  };
};

export default useSlideoutDateFilterState;
